import { diffLines, diffWordsWithSpace } from 'diff';
import {
  ChangeType,
  ProcessedLine,
  CollapsedSection,
  ProcessedDiff,
  InlineDiffSegment,
  InlineChangeType,
} from '../types';
import { NCTokenType } from '@components/common/NCFile/types';
import { CONTEXT_LINES, MIN_COLLAPSE_SIZE, INLINE_DIFF_THRESHOLD } from '@components/ncFileVersions/components/NCFileDiffView/constants/diffView.constants';

const splitChangeLines = (value: string): string[] => {
  const lines = value.split('\n');
  if (lines[lines.length - 1] === '') {
    lines.pop();
  }
  return lines;
};

const semanticPatterns: Array<{ type: NCTokenType; regex: RegExp }> = [
  { type: NCTokenType.COMMENT, regex: /\([^()]*\)/y },
  { type: NCTokenType.FMAX, regex: /FMAX/iy },
  { type: NCTokenType.VALUE, regex: /[A-Z]+[+\-]?\d+(?:\.\d+)?/y },
];

const splitBySemanticTokens = (text: string): { value: string; semanticType: NCTokenType }[] => {
  const result: { value: string; semanticType: NCTokenType }[] = [];
  let index = 0;

  const pushSegment = (value: string, semanticType: NCTokenType) => {
    if (!value) {
      return;
    }
    const prev = result[result.length - 1];
    if (prev && prev.semanticType === semanticType) {
      prev.value += value;
    } else {
      result.push({ value, semanticType });
    }
  };

  while (index < text.length) {
    let matched = false;
    for (const pattern of semanticPatterns) {
      pattern.regex.lastIndex = index;
      const match = pattern.regex.exec(text);
      if (match && match.index === index) {
        pushSegment(match[0], pattern.type);
        index += match[0].length;
        matched = true;
        break;
      }
    }

    if (!matched) {
      pushSegment(text[index], NCTokenType.DEFAULT);
      index += 1;
    }
  }

  return result;
};

const attachSemanticToSegments = (
  value: string,
  changeType: InlineChangeType
): InlineDiffSegment[] => {
  const semanticPieces = splitBySemanticTokens(value);
  return semanticPieces.map((piece) => ({
    value: piece.value,
    type: changeType,
    semanticType: piece.semanticType,
    isSemanticHighlight: changeType !== InlineChangeType.UNCHANGED && piece.semanticType !== NCTokenType.DEFAULT,
  }));
};

const buildInlineSegments = (oldLine: string, newLine: string): {
  oldSegments: InlineDiffSegment[];
  newSegments: InlineDiffSegment[];
} => {
  // Avoid heavy processing on very long lines
  if (oldLine.length + newLine.length > INLINE_DIFF_THRESHOLD) {
    return {
      oldSegments: attachSemanticToSegments(oldLine, InlineChangeType.UNCHANGED),
      newSegments: attachSemanticToSegments(newLine, InlineChangeType.UNCHANGED),
    };
  }

  const parts = diffWordsWithSpace(oldLine, newLine);
  const oldSegments: InlineDiffSegment[] = [];
  const newSegments: InlineDiffSegment[] = [];

  parts.forEach((part) => {
    if (part.added) {
      newSegments.push(...attachSemanticToSegments(part.value, InlineChangeType.ADDED));
    } else if (part.removed) {
      oldSegments.push(...attachSemanticToSegments(part.value, InlineChangeType.REMOVED));
    } else {
      const semanticSegments = attachSemanticToSegments(part.value, InlineChangeType.UNCHANGED);
      oldSegments.push(...semanticSegments);
      newSegments.push(...semanticSegments.map((segment) => ({ ...segment })));
    }
  });

  return { oldSegments, newSegments };
};

const generateSemanticSegmentsForContent = (
  content: string,
  changeType: InlineChangeType = InlineChangeType.UNCHANGED
): InlineDiffSegment[] => attachSemanticToSegments(content, changeType);

function processChangesToLines(oldText: string, newText: string): { oldLines: ProcessedLine[]; newLines: ProcessedLine[] } {
  const changes = diffLines(oldText, newText);

  const oldLines: ProcessedLine[] = [];
  const newLines: ProcessedLine[] = [];

  let oldLineNumber = 1;
  let newLineNumber = 1;

  for (let changeIndex = 0; changeIndex < changes.length; changeIndex += 1) {
    const change = changes[changeIndex];

    // Pair removed/added blocks to allow inline diffing
    if (
      change.removed &&
      changeIndex + 1 < changes.length &&
      changes[changeIndex + 1].added
    ) {
      const removedLines = splitChangeLines(change.value);
      const addedLines = splitChangeLines(changes[changeIndex + 1].value);
      const pairCount = Math.min(removedLines.length, addedLines.length);

      for (let pairIndex = 0; pairIndex < pairCount; pairIndex += 1) {
        const oldContent = removedLines[pairIndex];
        const newContent = addedLines[pairIndex];
        const { oldSegments, newSegments } = buildInlineSegments(oldContent, newContent);
        const key = `pair-${changeIndex}-${pairIndex}`;

        const oldLineNumberValue = oldLineNumber++;
        oldLines.push({
          oldLineNumber: oldLineNumberValue,
          newLineNumber: oldLineNumberValue,
          content: oldContent,
          changeType: ChangeType.REMOVED,
          key: `old-${key}`,
          inlineSegments: oldSegments,
        });

        const newLineNumberValue = newLineNumber++;
        newLines.push({
          oldLineNumber: newLineNumberValue,
          newLineNumber: newLineNumberValue,
          content: newContent,
          changeType: ChangeType.ADDED,
          key: `new-${key}`,
          inlineSegments: newSegments,
        });
      }

      // Handle leftover removed lines
      for (let r = pairCount; r < removedLines.length; r += 1) {
        const lineContent = removedLines[r];
        const key = `removed-${changeIndex}-${r}`;
        const oldLineNumberValue = oldLineNumber++;
        oldLines.push({
          oldLineNumber: oldLineNumberValue,
          newLineNumber: oldLineNumberValue,
          content: lineContent,
          changeType: ChangeType.REMOVED,
          key: `old-${key}`,
        });
        newLines.push({
          oldLineNumber: oldLineNumberValue,
          newLineNumber: oldLineNumberValue,
          content: '',
          changeType: ChangeType.ADDED,
          key: `new-placeholder-${key}`,
        });
      }

      // Handle leftover added lines
      for (let a = pairCount; a < addedLines.length; a += 1) {
        const lineContent = addedLines[a];
        const key = `added-${changeIndex}-${a}`;
        const newLineNumberValue = newLineNumber++;
        newLines.push({
          oldLineNumber: newLineNumberValue,
          newLineNumber: newLineNumberValue,
          content: lineContent,
          changeType: ChangeType.ADDED,
          key: `new-${key}`,
        });
        oldLines.push({
          oldLineNumber: newLineNumberValue,
          newLineNumber: newLineNumberValue,
          content: '',
          changeType: ChangeType.ADDED,
          key: `old-placeholder-${key}`,
        });
      }

      changeIndex += 1; // Skip paired added block
      continue;
    }

    const lines = splitChangeLines(change.value);

    lines.forEach((lineContent, lineIndex) => {
      const key = `change-${changeIndex}-line-${lineIndex}`;

      if (change.added) {
        const newLineNumberValue = newLineNumber++;
        newLines.push({
          oldLineNumber: newLineNumberValue,
          newLineNumber: newLineNumberValue,
          content: lineContent,
          changeType: ChangeType.ADDED,
          key: `new-${key}`,
        });
        oldLines.push({
          oldLineNumber: newLineNumberValue,
          newLineNumber: newLineNumberValue,
          content: '',
          changeType: ChangeType.ADDED,
          key: `old-placeholder-${key}`,
        });
      } else if (change.removed) {
        const oldLineNumberValue = oldLineNumber++;
        oldLines.push({
          oldLineNumber: oldLineNumberValue,
          newLineNumber: oldLineNumberValue,
          content: lineContent,
          changeType: ChangeType.REMOVED,
          key: `old-${key}`,
        });
        newLines.push({
          oldLineNumber: oldLineNumberValue,
          newLineNumber: oldLineNumberValue,
          content: '',
          changeType: ChangeType.ADDED,
          key: `new-placeholder-${key}`,
        });
      } else {
        const semanticSegments = generateSemanticSegmentsForContent(lineContent);
        oldLines.push({
          oldLineNumber: oldLineNumber++,
          newLineNumber: newLineNumber,
          content: lineContent,
          changeType: ChangeType.UNCHANGED,
          key: `old-${key}`,
          inlineSegments: semanticSegments,
        });
        newLines.push({
          oldLineNumber: oldLineNumber - 1,
          newLineNumber: newLineNumber++,
          content: lineContent,
          changeType: ChangeType.UNCHANGED,
          key: `new-${key}`,
          inlineSegments: semanticSegments.map((segment) => ({ ...segment })),
        });
      }
    });
  }

  const rowCount = Math.max(oldLines.length, newLines.length);
  for (let i = 0; i < rowCount; i += 1) {
    const displayNumber = i + 1;
    if (oldLines[i]) {
      oldLines[i].displayLineNumber = displayNumber;
    }
    if (newLines[i]) {
      newLines[i].displayLineNumber = displayNumber;
    }
  }

  return { oldLines, newLines };
}

// Find indices of all changed lines
function findChangedLineIndices(lines: ProcessedLine[]): number[] {
  const changedIndices: number[] = [];
  lines.forEach((line, index) => {
    if (line.changeType !== ChangeType.UNCHANGED) {
      changedIndices.push(index);
    }
  });
  return changedIndices;
}

// Determine which lines should be visible based on changes and context
function determineVisibleLines(lines: ProcessedLine[]): Set<number> {
  const visibleIndices = new Set<number>();
  const changedIndices = findChangedLineIndices(lines);

  if (changedIndices.length === 0) {
    // If no changes, show all lines (shouldn't happen in a diff, but handle it)
    lines.forEach((_, index) => visibleIndices.add(index));
    return visibleIndices;
  }

  // Add changed lines and their context
  changedIndices.forEach((changeIndex) => {
    // Add the changed line itself
    visibleIndices.add(changeIndex);

    // Add context before
    for (let i = Math.max(0, changeIndex - CONTEXT_LINES); i < changeIndex; i++) {
      visibleIndices.add(i);
    }

    // Add context after
    for (let i = changeIndex + 1; i <= Math.min(lines.length - 1, changeIndex + CONTEXT_LINES); i++) {
      visibleIndices.add(i);
    }
  });

  // Fill in gaps that are too small to collapse
  const sortedVisible = Array.from(visibleIndices).sort((a, b) => a - b);
  for (let i = 0; i < sortedVisible.length - 1; i++) {
    const current = sortedVisible[i];
    const next = sortedVisible[i + 1];
    const gapSize = next - current - 1;

    // If gap is 9 lines or less, show all lines in between
    if (gapSize > 0 && gapSize < MIN_COLLAPSE_SIZE) {
      for (let j = current + 1; j < next; j++) {
        visibleIndices.add(j);
      }
    }
  }

  return visibleIndices;
}

// Create collapsed sections from non-visible lines
function createCollapsedSections(lines: ProcessedLine[], visibleIndices: Set<number>): (ProcessedLine | CollapsedSection)[] {
  const result: (ProcessedLine | CollapsedSection)[] = [];
  let i = 0;

  while (i < lines.length) {
    if (visibleIndices.has(i)) {
      // This line is visible, add it
      result.push(lines[i]);
      i++;
    } else {
      // Start of a collapsed section
      const collapseStart = i;
      while (i < lines.length && !visibleIndices.has(i)) {
        i++;
      }
      const collapseEnd = i;
      const count = collapseEnd - collapseStart;

      // Only create a collapsed section if it's large enough
      if (count >= MIN_COLLAPSE_SIZE) {
        // Get line numbers for the collapsed section
        const startLine = lines[collapseStart];
        const collapsedSection: CollapsedSection = {
          startLineOld: startLine.oldLineNumber || 0,
          startLineNew: startLine.newLineNumber || 0,
          count,
          key: `collapsed-${collapseStart}-${collapseEnd}`,
        };
        result.push(collapsedSection);
      } else {
        // If section is too small, add all lines individually
        for (let j = collapseStart; j < collapseEnd; j++) {
          result.push(lines[j]);
        }
      }
    }
  }

  return result;
}

// Main function to process diff with context and collapsing
export function processDiffWithContext(oldText: string, newText: string): ProcessedDiff {
  // Step 1: Get line-by-line diff
  const { oldLines, newLines } = processChangesToLines(oldText, newText);

  // Step 2: Determine which lines should be visible
  // Use old lines to determine visibility (both should have same structure)
  const visibleIndices = determineVisibleLines(oldLines);

  // Step 3: Create result with collapsed sections
  const oldResult = createCollapsedSections(oldLines, visibleIndices);
  const newResult = createCollapsedSections(newLines, visibleIndices);

  return {
    oldLines: oldResult,
    newLines: newResult,
  };
}

// Type guard to check if an item is a CollapsedSection
export function isCollapsedSection(item: ProcessedLine | CollapsedSection | null | undefined): item is CollapsedSection {
  return !!item && 'count' in item && 'startLineOld' in item;
}

// Expand a collapsed section back into its original lines
export function expandCollapsedSection(allLines: ProcessedLine[], section: CollapsedSection): ProcessedLine[] {
  // Find the start index
  let startIndex = -1;
  for (let i = 0; i < allLines.length; i++) {
    const line = allLines[i];
    if (
      (section.startLineOld > 0 && line.oldLineNumber === section.startLineOld) ||
      (section.startLineNew > 0 && line.newLineNumber === section.startLineNew)
    ) {
      startIndex = i;
      break;
    }
  }

  if (startIndex === -1) {
    return [];
  }

  return allLines.slice(startIndex, startIndex + section.count);
}

export const truncateContent = (content: string, limit: number): string => {
  let lineCount = 0;
  let index = 0;
  while (lineCount < limit && index < content.length) {
    const nextNewline = content.indexOf('\n', index);
    if (nextNewline === -1) {
      break;
    }
    index = nextNewline + 1;
    lineCount++;
  }
  return index < content.length ? content.substring(0, index) : content;
};
